import { PauseOutlined, CaretRightOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { pauseOtherCanvasAudio, clearCanvasAudioPlayback } from './canvasAudioPlaybackCoordinator'
import { formatAudioTimeMs } from './audioTrimTime'

type Props = {
  playbackUrl: string
  testId?: string
  title?: string | null
}

// VistoeraTASK-00171 — title is plain (non-interactive) chrome now: the
// node body's click-to-expand is owned by the SAME canonical
// `onNodeClick`/`onExpandFunctionCardDetail` primitive Image/Video Asset
// use (see `AudioAssetNode.tsx`'s own doc comment), so a bare click on
// this title naturally bubbles up and expands — no local button/handler
// needed here to "forward" that click anymore (TASK-00168's stopgap).
export default function CanvasAudioAssetCompactPlayer({ playbackUrl, testId, title }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentMs, setCurrentMs] = useState(0)
  const [durationMs, setDurationMs] = useState(0)

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const onLoaded = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) {
        setDurationMs(Math.round(el.duration * 1000))
      }
    }
    const onTime = () => setCurrentMs(Math.round(el.currentTime * 1000))
    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onEnded = () => {
      setPlaying(false)
      setCurrentMs(0)
    }
    el.addEventListener('loadedmetadata', onLoaded)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', onEnded)
    return () => {
      el.removeEventListener('loadedmetadata', onLoaded)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('ended', onEnded)
      clearCanvasAudioPlayback(el)
    }
  }, [playbackUrl])

  const stopBubble = (e: ReactMouseEvent) => {
    e.stopPropagation()
  }

  const togglePlay = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      pauseOtherCanvasAudio(el)
      void el.play()
    } else {
      el.pause()
    }
  }, [])

  const onSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = audioRef.current
      if (!el || !durationMs) return
      const ratio = Number(e.target.value) / 1000
      el.currentTime = (durationMs / 1000) * ratio
      setCurrentMs(Math.round(el.currentTime * 1000))
    },
    [durationMs],
  )

  const progress = durationMs > 0 ? Math.round((currentMs / durationMs) * 1000) : 0

  return (
    <div className="pcv-audio-asset-compact-player">
      {title ? (
        <div className="pcv-audio-asset-name" title={title}>
          {title}
        </div>
      ) : null}
      <div className="pcv-audio-asset-compact-row nodrag" onClick={stopBubble}>
        <button
          type="button"
          className="pcv-audio-asset-compact-play"
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={togglePlay}
        >
          {playing ? <PauseOutlined /> : <CaretRightOutlined />}
        </button>
        <span className="pcv-audio-asset-compact-time pcv-audio-asset-compact-time--current">
          {formatAudioTimeMs(currentMs)}
        </span>
        <input
          type="range"
          className="pcv-audio-asset-compact-seek"
          min={0}
          max={1000}
          value={progress}
          aria-label="Playback position"
          onChange={onSeek}
          onPointerDown={stopBubble}
        />
        <span className="pcv-audio-asset-compact-time pcv-audio-asset-compact-time--duration">
          {formatAudioTimeMs(durationMs)}
        </span>
      </div>
      <audio ref={audioRef} src={playbackUrl} preload="metadata" data-testid={testId} />
    </div>
  )
}
